import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../api';
import type { Printer, RegistrationCode } from '../components/printers';

interface UsePrintersOptions {
  autoRefresh?: boolean;
}

interface UsePrintersReturn {
  printers: Printer[];
  loading: boolean;
  error: string | null;
  registrationCode: RegistrationCode | null;
  creatingCode: boolean;
  refresh: () => Promise<void>;
  requestRegistrationCode: (label: string, expiresMinutes?: number) => Promise<void>;
  clearRegistrationCode: () => void;
  deletePrinter: (printerId: string) => Promise<void>;
}

const DEFAULT_EXPIRES = 60;

export const usePrinters = (options: UsePrintersOptions = {}): UsePrintersReturn => {
  const { autoRefresh = true } = options;
  const api = useApi();
  const { t } = useTranslation();

  const [printers, setPrinters] = useState<Printer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registrationCode, setRegistrationCode] = useState<RegistrationCode | null>(null);
  const [creatingCode, setCreatingCode] = useState(false);

  const fetchPrinters = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data: Printer[] = await api.get('/printers');
      setPrinters(data);
    } catch (err) {
      console.error('Failed to fetch printers', err);
      setError(t('printing.fetchFailed'));
      setPrinters([]);
    } finally {
      setLoading(false);
    }
  }, [api, t]);

  const requestRegistrationCode = useCallback(
    async (label: string, expiresMinutes: number = DEFAULT_EXPIRES) => {
      const trimmed = label.trim();
      if (!trimmed) {
        setError(t('printing.labelRequired'));
        return;
      }
      try {
        setCreatingCode(true);
        setError(null);
        const minutes = Math.min(Math.max(expiresMinutes, 5), 7 * 24 * 60);
        const payload = { label: trimmed, expires_minutes: minutes };
        const data: RegistrationCode = await api.post('/printers/registration-codes', payload);
        setRegistrationCode(data);
      } catch (err) {
        console.error('Failed to create registration code', err);
        setError(t('printing.generateFailed'));
      } finally {
        setCreatingCode(false);
      }
    },
    [api, t]
  );

  const clearRegistrationCode = useCallback(() => {
    setRegistrationCode(null);
  }, []);

  const deletePrinter = useCallback(
    async (printerId: string) => {
      try {
        setError(null);
        await api.del(`/printers/${printerId}`);
        // Remove the printer from local state immediately
        setPrinters(prev => prev.filter(p => p.id !== printerId));
      } catch (err) {
        console.error('Failed to delete printer', err);
        setError(t('printing.deleteFailed'));
        // Refresh the list to ensure consistency
        await fetchPrinters();
      }
    },
    [api, t, fetchPrinters]
  );

  useEffect(() => {
    if (autoRefresh) {
      void fetchPrinters();
    }
  }, [autoRefresh, fetchPrinters]);

  return {
    printers,
    loading,
    error,
    registrationCode,
    creatingCode,
    refresh: fetchPrinters,
    requestRegistrationCode,
    clearRegistrationCode,
    deletePrinter,
  };
};
